/**
 * Generic social stats utilities for processing social stats data
 */

import type { SocialStatsDataPoint, SocialStatsChartDataItem, SocialStatsTransformFunction } from '@/types/social-stats';

/**
 * Transform raw social stats data points into chart data format
 * @param rawData - Array of social stats data points
 * @returns Array of chart data items
 */
export const transformSocialStatsToChartData: SocialStatsTransformFunction = (rawData: SocialStatsDataPoint[]): SocialStatsChartDataItem[] => {
  return rawData.reduce((acc: SocialStatsChartDataItem[], item) => {
    if (item.date != null && item.value != null) {
      try {
        // Parse the date, handling both string and Date object inputs
        const date = item.date instanceof Date ? item.date : new Date(item.date);
        // Validate the date is valid
        if (isNaN(date.getTime())) {
          console.warn(`Invalid date encountered: ${item.date}`);
          return acc;
        }

        // Format to YYYY-MM-DD in UTC to ensure consistency
        const dateString = date.toISOString().split('T')[0];
        acc.push({
          xValue: dateString,
          yValue: item.value,
          ...item, // Include any additional properties
        });
      } catch (error) {
        console.warn(`Error processing date ${item.date}:`, error);
        return acc;
      }
    }
    return acc;
  }, []);
};

/**
 * Filter social stats data by date range
 * @param data - Array of social stats data points
 * @param startDate - Start date (inclusive)
 * @param endDate - End date (inclusive)
 * @returns Filtered array of social stats data points
 */
export const filterSocialStatsByDateRange = (
  data: SocialStatsDataPoint[],
  startDate: Date,
  endDate: Date
): SocialStatsDataPoint[] => {
  return data.filter((item) => {
    if (!item.date) return false;

    try {
      const date = item.date instanceof Date ? item.date : new Date(item.date);
      return date >= startDate && date <= endDate;
    } catch (error) {
      console.warn(`Error filtering date ${item.date}:`, error);
      return false;
    }
  });
};

/**
 * Sort social stats data by date
 * @param data - Array of social stats data points
 * @param ascending - Whether to sort in ascending order (default: true)
 * @returns Sorted array of social stats data points
 */
export const sortSocialStatsByDate = (
  data: SocialStatsDataPoint[],
  ascending: boolean = true
): SocialStatsDataPoint[] => {
  return [...data].sort((a, b) => {
    if (!a.date || !b.date) return 0;

    try {
      const dateA = a.date instanceof Date ? a.date : new Date(a.date);
      const dateB = b.date instanceof Date ? b.date : new Date(b.date);

      return ascending
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    } catch (error) {
      console.warn('Error sorting dates:', error);
      return 0;
    }
  });
};

/**
 * Calculate basic statistics for social stats data
 * @param data - Array of social stats data points
 * @returns Object with basic statistics
 */
export const calculateSocialStatsBasics = (data: SocialStatsDataPoint[]) => {
  if (data.length === 0) {
    return {
      total: 0,
      average: 0,
      min: 0,
      max: 0,
      count: 0,
    };
  }

  const values = data.map((item) => item.value).filter((value) => value != null);
  const sum = values.reduce((acc, value) => acc + value, 0);

  return {
    total: sum,
    average: sum / values.length,
    min: Math.min(...values),
    max: Math.max(...values),
    count: values.length,
  };
};

/**
 * Validate social stats data point
 * @param dataPoint - Social stats data point to validate
 * @returns Boolean indicating if the data point is valid
 */
export const isValidSocialStatsDataPoint = (dataPoint: any): dataPoint is SocialStatsDataPoint => {
  return (
    dataPoint != null &&
    typeof dataPoint === 'object' &&
    (dataPoint.date != null) &&
    (typeof dataPoint.value === 'number') &&
    !isNaN(dataPoint.value)
  );
};

/**
 * Clean and validate social stats data
 * @param data - Array of potentially invalid social stats data
 * @returns Array of validated social stats data points
 */
export const cleanSocialStatsData = (data: any[]): SocialStatsDataPoint[] => {
  if (!Array.isArray(data)) {
    console.warn('Social stats data is not an array');
    return [];
  }

  return data.filter(isValidSocialStatsDataPoint);
};
